export type Met = 'recommended' | 'uncertain' | 'not_recommended'

export type MetChange =
  | { kind: 'new' }
  | { kind: 'unchanged' }
  | { direction: 'up' | 'down'; from: Met; kind: 'changed'; to: Met; }

// Higher rank = "more recommended". Used to decide whether a change is an
// improvement (up) or a downgrade (down) for badge styling.
const RANK: Record<Met, number> = {
  not_recommended: 0,
  recommended: 2,
  uncertain: 1,
}

/**
 * Diffs a current met value against the previous run's value (from `lastRun`).
 * Returns undefined when there's nothing to show (no current value).
 *   - no previous  → 'new'
 *   - equal        → 'unchanged'
 *   - otherwise    → 'changed' with direction
 */
export function metChange(current?: Met, previous?: Met): MetChange | undefined {
  if (!current) return undefined
  if (!previous) return { kind: 'new' }
  if (previous === current) return { kind: 'unchanged' }

  return {
    direction: RANK[current] > RANK[previous] ? 'up' : 'down',
    from: previous,
    kind: 'changed',
    to: current,
  }
}

/** Builds a quick lookup of a previous run's per-service met values by id. */
export function lastRunMetById(
  services?: { id: string; met: Met }[],
): Record<string, Met> {
  const map: Record<string, Met> = {}

  for (const service of services ?? []) map[service.id] = service.met

  return map
}

// UI display order: Passar, Osäkert, Passar inte, then services without an assessment yet.
const MET_SORT_ORDER: Record<Met, number> = {
  not_recommended: 3,
  recommended: 1,
  uncertain: 2,
}

export function metSortOrder(met?: Met): number {
  return met ? MET_SORT_ORDER[met] : 999
}

/** Sorts services by their current assessment (Passar, Osäkert, Passar inte), matching the "Hitta stöd" page order. */
export function sortServicesByMet<T extends { id: string }>(
  services: T[],
  generationServices?: { id: string; met?: Met }[],
): T[] {
  const metById = new Map(generationServices?.map(s => [s.id, s.met]))

  return [...services].sort(
    (a, b) => metSortOrder(metById.get(a.id)) - metSortOrder(metById.get(b.id)),
  )
}
